import React from 'react';
import { cx } from './cx';

export const progressVariants = ['default', 'green', 'red', 'blue'] as const;
export const progressSizes = ['small', 'medium', 'large'] as const;

export type ProgressVariant = (typeof progressVariants)[number];
export type ProgressSize = (typeof progressSizes)[number];

interface ProgressBarProps {
  value: number;
  max?: number;
  label?: string;
  showPercentage?: boolean;
  variant?: ProgressVariant;
  size?: ProgressSize;
  className?: string;
}

function variantClass(variant: ProgressVariant): string {
  switch (variant) {
    case 'default':
      return 'osrs-progress--default';
    case 'green':
      return 'osrs-progress--green';
    case 'red':
      return 'osrs-progress--red';
    case 'blue':
      return 'osrs-progress--blue';
    default: {
      const neverVariant: never = variant;
      return neverVariant;
    }
  }
}

function sizeClass(size: ProgressSize): string {
  switch (size) {
    case 'small':
      return 'osrs-progress--small';
    case 'medium':
      return 'osrs-progress--medium';
    case 'large':
      return 'osrs-progress--large';
    default: {
      const neverSize: never = size;
      return neverSize;
    }
  }
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  max = 100,
  label,
  showPercentage = false,
  variant = 'default',
  size = 'medium',
  className,
}) => {
  const safeMax = max <= 0 ? 1 : max;
  const clamped = Math.min(safeMax, Math.max(0, value));
  const percentage = (clamped / safeMax) * 100;

  return (
    <div className={cx('osrs-progress-block', className)}>
      {label ? <div>{label}</div> : null}
      <div
        className={cx('osrs-progress', variantClass(variant), sizeClass(size))}
        role="progressbar"
        aria-valuenow={Math.round(clamped)}
        aria-valuemin={0}
        aria-valuemax={safeMax}
        aria-label={label}
      >
        <div className="osrs-progress__fill" style={{ width: `${percentage}%` }} />
      </div>
      {showPercentage ? <div>{Math.round(percentage)}%</div> : null}
    </div>
  );
};

export default ProgressBar;
